import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, HelpCircle, Mail, XCircle } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, Notice } from "../parts";
import { formatMoney } from "../money";
import {
  REFRESH_FAILED,
  RECONCILIATION_POLL_MS,
  checksMade,
  keepPolling,
  pollAgain,
  readReconciliationQuery,
  statusScreen,
  type ReconciliationPayment,
} from "./reconciliation/reconciliation";

// /basket/reconciliation: where a payment link the school sent lands after
// Stripe (?payment_id=&checkout_session_id=, or ?expired=1, or
// ?status=cancelled). Port of v1's ReconciliationClient, with its copy:
// the payment's status is asked every 3 seconds for 30 seconds, then by
// hand. The status answer names no school, so it can't be refused on
// another school's site (the checkout session is what lets the customer in).

export function ReconciliationView({ ctx }: { ctx: ViewContext }) {
  // The query is the page's address: read once.
  const [query] = useState(() => readReconciliationQuery(window.location.search));
  const qc = useQueryClient();
  const paymentId = query.kind === "poll" || query.kind === "expired" ? query.paymentId : undefined;
  const session = query.kind === "poll" || query.kind === "expired" ? query.session : undefined;
  const key = ["booking", "reconciliation", ctx.api, paymentId, session];
  const status = useQuery({
    queryKey: key,
    queryFn: () =>
      bookingJson<ReconciliationPayment>(
        `${ctx.api}/widget/app/basket-reconciliation/${encodeURIComponent(paymentId!)}/status?session=${encodeURIComponent(session!)}`,
      ),
    enabled: query.kind === "poll",
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
    refetchInterval: (q) => (pollAgain(q.state) ? RECONCILIATION_POLL_MS : false),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
  const refresh = useMutation({
    mutationFn: () =>
      sendJson(`${ctx.api}/widget/app/basket-reconciliation/${encodeURIComponent(paymentId!)}/request-refresh`, "POST", { session }),
  });

  const screen = statusScreen(status.data, status.error);
  const home = ctx.links.home();
  const back = (name?: string, primary = false) => (
    <Button asChild variant={primary ? "default" : "outline"} className="w-full">
      <a href={home}>{primary ? `Continue to ${name || "homepage"}` : `Back to ${name || "homepage"}`}</a>
    </Button>
  );

  if (query.kind === "not-found" || (query.kind === "poll" && screen.kind === "not-found")) {
    return (
      <Panel icon={<HelpCircle className="size-9 text-muted-foreground" />} tone="muted" title="We couldn’t find this payment">
        <p className="mb-8 text-muted-foreground">
          The link may have expired or be invalid. Please use the most recent link from your email, or contact the school for help.
        </p>
        {back()}
      </Panel>
    );
  }

  if (query.kind === "expired") {
    if (refresh.isSuccess) {
      return (
        <Panel icon={<CheckCircle2 className="size-9 text-green-600" />} tone="success" title="Request sent">
          <p className="mb-8 text-muted-foreground">We’ve let the school know. They’ll send you a fresh payment link by email shortly.</p>
          {back()}
        </Panel>
      );
    }
    return (
      <Panel icon={<AlertTriangle className="size-9 text-amber-600" />} tone="warning" title="This payment link has expired">
        <p className="mb-8 text-muted-foreground">
          For security, payment links expire after a short time.
          {paymentId && session ? " Tap the button below to ask the school for a fresh link." : " Please ask the school for a fresh link."}
        </p>
        {refresh.isError && (
          <div className="mb-4 text-left">
            <Notice tone="error">{messageOf(refresh.error, REFRESH_FAILED)}</Notice>
          </div>
        )}
        {paymentId && session && (
          <Button onClick={() => refresh.mutate()} disabled={refresh.isPending} className="mb-2 w-full">
            {refresh.isPending ? "Sending request…" : "Request a new link"}
          </Button>
        )}
        {back()}
      </Panel>
    );
  }

  if (query.kind === "cancelled") {
    return (
      <Panel icon={<XCircle className="size-9 text-red-600" />} tone="danger" title="Payment cancelled">
        <p className="mb-8 text-muted-foreground">
          Your payment to the school was cancelled and you have not been charged. If this was a mistake, please use the link in the original
          email to try again, or contact the school directly.
        </p>
        {back()}
      </Panel>
    );
  }

  if (screen.kind === "loading" || screen.kind === "not-found") return <BookingLoading rows={1} />;
  if (screen.kind === "error") {
    return (
      <Panel icon={<AlertTriangle className="size-9 text-amber-600" />} tone="warning" title="Something went wrong">
        <p className="mb-8 text-muted-foreground">{screen.message}</p>
        {back()}
      </Panel>
    );
  }

  const data = screen.payment;
  const amount = formatMoney(data.amount, data.currency);
  if (data.status === "SUCCEEDED") {
    return (
      <Panel icon={<CheckCircle2 className="size-9 text-green-600" />} tone="success" title="Payment received">
        <p className="mb-6 text-muted-foreground">
          Thanks — we’ve received your payment to {data.schoolName}. You’ll get a confirmation email shortly with the receipt.
        </p>
        <div className="mb-6 space-y-2 rounded-lg bg-muted/50 p-4 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium">{amount}</span>
          </div>
          {data.basketOrderShortId && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Order</span>
              <span className="font-mono text-xs">#{data.basketOrderShortId}</span>
            </div>
          )}
          {data.reason && (
            <div>
              <div className="mb-1 text-muted-foreground">For</div>
              <div>{data.reason}</div>
            </div>
          )}
        </div>
        <div className="mb-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Mail className="size-4" />
          <span>A receipt will be sent to your email.</span>
        </div>
        {back(data.schoolName, true)}
      </Panel>
    );
  }

  if (data.status === "FAILED") {
    return (
      <Panel icon={<XCircle className="size-9 text-red-600" />} tone="danger" title="Payment didn’t go through">
        <p className="mb-8 text-muted-foreground">
          We couldn’t process your payment to {data.schoolName}. No charge has been made. Please try the link in your email again, or contact{" "}
          {data.schoolName} for a new payment link.
        </p>
        {back(data.schoolName)}
      </Panel>
    );
  }

  // PENDING / PROCESSING: the webhook hasn't landed yet. After 30 seconds
  // of checks, one more is by hand.
  const counters = qc.getQueryState(key);
  const stalled = !keepPolling(data, counters ? checksMade(counters) : 0);
  return (
    <Panel icon={<Spinner className="size-8" />} tone="none" title="Confirming your payment…">
      <p className="mb-6 text-muted-foreground">
        We’ve received your payment of <span className="font-medium text-foreground">{amount}</span> to {data.schoolName} and are
        confirming with your bank — this usually takes a few seconds.
      </p>
      <p className="mb-6 text-sm text-muted-foreground">You can safely close this page; we’ll email you the receipt.</p>
      {stalled && (
        <Button variant="outline" className="mb-2 w-full" disabled={status.isFetching} onClick={() => void status.refetch()}>
          {status.isFetching ? "Checking…" : "Check again"}
        </Button>
      )}
      {back(data.schoolName, true)}
    </Panel>
  );
}

const TONES = {
  none: "",
  muted: "bg-muted",
  success: "bg-green-100",
  warning: "bg-amber-100",
  danger: "bg-red-100",
} as const;

function Panel({ icon, tone, title, children }: { icon: ReactNode; tone: keyof typeof TONES; title: string; children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-lg rounded-2xl border bg-card p-8 text-center shadow-sm">
      <div className={`mx-auto mb-6 flex size-16 items-center justify-center rounded-full ${TONES[tone]}`}>{icon}</div>
      <h1 className="mb-3 text-2xl font-semibold">{title}</h1>
      {children}
    </div>
  );
}
